import Handsontable from 'handsontable/base';
import { HotTableRef } from '@handsontable/react-wrapper';
import { RefObject } from 'react';
import { ErrorState } from 'types';
import {
  ERROR_HEADER,
  ERROR_INPUT,
  ERROR_ROW
} from '@utils/table-validations/constants';

// Each mounted lot owns its decoration history.
const previousErrors = new WeakMap<Handsontable, ErrorState>();

const errors = { header: ERROR_HEADER, input: ERROR_INPUT, row: ERROR_ROW };

/**
 * Adds or removes error classes to a specific element based on the provided
 * error class name.
 *
 * @param element - The HTML element to which the error class will be added or removed.
 * @param error - The error class name to be added or removed.
 * @param adding - A boolean indicating whether to add or remove the error class.
 *                Defaults to true (adding).
 */
const updateErrorClass = (element: HTMLElement, error: string, adding = true): void => {
  if (!element) return;

  element.classList[adding ? 'add' : 'remove'](error);
};

/**
 * Applies error state to a specific row header in the table by adding or removing
 * error classes to the row header element.
 *
 * @param errorState {ErrorState} - The error state object containing row errors.
 * @param hotInstance {Handsontable} - The Handsontable instance.
 */
export const updateErrorStateOnRowHeader = (errorState: ErrorState, hotInstance: Handsontable) => {
  const errorRows = Object.keys(errorState || {});

  hotInstance.updateSettings({
    afterGetRowHeaderRenderers: headerRenderers => {
      // Store all original renderers
      const originalRenderers = [...headerRenderers];

      const newHeaderRenderers = originalRenderers.map(renderer => (
        (row: number, TH: HTMLTableCellElement) => {
          renderer(row, TH);

          // Remove the error class first (always)
          TH.classList.remove(errors.header);

          // Then add it back only if this row has errors
          if (errorRows.includes(row.toString())) {
            TH.classList.add(errors.header);
          }
        }
      ));

      // Replace all elements in the array without reassigning.
      headerRenderers.splice(0, headerRenderers.length, ...(newHeaderRenderers as typeof headerRenderers));
    }
  });

  hotInstance.render();
};

/**
 * Applies error state to a specific row in the table by adding or removing
 * error classes to the row's parent element.
 *
 * @param hotInstance - The Handsontable instance.
 * @param row - The row index of the cell.
 * @param adding - A boolean indicating whether to add or remove the error class.
 */
export const updateErrorStateOnRow = (
  hotInstance: Handsontable,
  row: number,
  adding = true,
): void => {
  const currentCell = hotInstance.getCell(row, 1);
  if (!currentCell || !currentCell.parentElement) return;
  updateErrorClass(currentCell.parentElement, errors.row, adding);
};

/**
 * Applies error state to a specific cell in the table by adding or removing
 * error classes to the cell's child div.
 *
 * @param hotInstance - The Handsontable instance.
 * @param row - The row index of the cell.
 * @param col - The column index of the cell.
 * @param adding - A boolean indicating whether to add or remove the error class.
 */
export const updateErrorStateOnCell = (
  hotInstance: Handsontable,
  row: number,
  col: number,
  adding = true,
): void => {
  const currentCell = hotInstance.getCell(row, col);

  if (!currentCell?.getElementsByTagName('div')?.length) return;
  const cellChildDiv = currentCell.getElementsByTagName('div')[0];

  updateErrorClass(cellChildDiv, errors.input, adding);
};

/**
 * Clears error classes only from the affected rows and columns (not the entire table).
 *
 * @param nextErrorState {ErrorState} - The new error state object.
 * @param hotInstance {Handsontable} - The Handsontable instance.
 */
export const clearErrorStateFromAffected = (
  nextErrorState: ErrorState,
  hotInstance: Handsontable
): void => {
  const previous = previousErrors.get(hotInstance) ?? {};
  // Get all rows that had errors before or will have errors now
  const affectedRows = new Set([
    ...Object.keys(previous),
    ...Object.keys(nextErrorState || {})
  ]);

  affectedRows.forEach((key: string): void => {
    const row = Number(key);
    // Remove row error class
    updateErrorStateOnRow(hotInstance, row, false);

    // Remove cell error classes for all columns that had errors before
    (previous[row] || []).forEach((col: number) => {
      updateErrorStateOnCell(hotInstance, row, col, false);
    });

    // Remove row header error class if this row is no longer in nextErrorState
    if (!nextErrorState[row] || nextErrorState[row].length === 0) {
      // Remove error class from row header cell using Handsontable public API
      const headerCell = hotInstance.getCell(row, -1);
      if (headerCell) {
        headerCell.classList.remove(errors.header);
      }
    }
  });
};

interface DrawErrorStateParams {
  errorState: ErrorState;
  hotRef: RefObject<HotTableRef>;
}

/**
 * Applies error state to the table based on the provided error state to highlight
 * the rows header, row and cells that have errors.
 *
 * @param errorState - The error state object containing row and column indices with errors.
 * @param hotRef - A reference to the Handsontable instance.
 */
export const applyErrorState = ({ errorState, hotRef }: DrawErrorStateParams): void => {
  const hotInstance = hotRef.current?.hotInstance;
  if (!hotInstance) return;

  // Only clear affected rows/cells
  clearErrorStateFromAffected(errorState, hotInstance);

  // Always update row header renderers for the new error state
  updateErrorStateOnRowHeader(errorState, hotInstance);

  if (!errorState || !Object.keys(errorState).length) {
    previousErrors.delete(hotInstance);
    return;
  }

  // Apply error classes to new error state (rows and cells)
  Object.keys(errorState).forEach((key: string): void => {
    const row = Number(key);
    updateErrorStateOnRow(hotInstance, row);
    const columns = errorState[row];
    columns.forEach((col: number): void => {
      updateErrorStateOnCell(hotInstance, row, col);
    });
  });

  // Update previous error state
  previousErrors.set(hotInstance, Object.fromEntries(Object.entries(errorState).map(([row, columns]) => [row, [...columns]])));
};
